<script setup lang="ts">
import { computed, ref } from "vue";
import { useSorted } from "@vueuse/core";
import { useI18n } from "vue-i18n";

import Notifications from "@/components/Reusables/Utils/Notifications.vue";
import Popover from "@/components/Reusables/Popover/Popover.vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import { Funnel } from "@lucide/vue";
import FilterGroupPanel from "./FilterGroupPanel.vue";
import FilterGroup from "./FilterGroup.vue";
import FilterGroupHeader from "./FilterGroupHeader.vue";
import FilterOption from "./FilterOption.vue";

type ActiveFilters = Record<string, Record<string, boolean>>;
type GroupValue = unknown;
type GroupConfig = {
  label: string;
  itemsOf?: (val: GroupValue) => any[];
  getId?: (item: any) => string;
  getLabel?: (item: any) => string;
  showSearch: boolean;
};

const { t } = useI18n();

const props = defineProps<{
  filterGroups: Record<string, GroupValue>;
  direction?: string;
  search: boolean;
  extraActiveCount?: number;
  groupLabels?: Record<string, string>;
  // Accessible label for the trigger button and panel. Callers already pass `title`;
  // it is not rendered as visible copy (the panel header stays "Filters").
  title?: string;
  // Optional DOM id for the trigger button, so callers can anchor a flow-guide
  // step to it. Renders no attribute when omitted.
  triggerId?: string;
  // Forwarded to the underlying Popover. Toolbars that can be overlapped by the AI Agent window
  // pass `teleport` + `panelZ="z-modal"` so the panel floats above it. Omitted elsewhere, so every
  // other FilterBox keeps the default inline `z-dropdown` panel.
  teleport?: boolean;
  panelZ?: string;
  // Trigger box sizing; the Library toolbar default. A denser host (the EPD picker) passes a fixed
  // square that lines up with its shorter search input.
  triggerSizeClass?: string;
  // Per group: option ids shown greyed, unpickable and listed last, with an optional tooltip reason.
  disabledItems?: Record<string, { ids: string[]; reason?: string }>;
  // Groups whose options are still loading: their panel shows a spinner instead of the options.
  loadingGroups?: string[];
}>();

const emit = defineEmits<{
  (e: "update:activeFilters", v: ActiveFilters): void;
  (e: "clear"): void;
}>();

const activeFilters = defineModel<ActiveFilters>("activeFilters", {
  default: () => ({}),
});

const activeGroup = ref<string | null>(null);

// Popover handles open/close, outside-click, Esc and positioning. `direction` maps to
// the panel's horizontal alignment: default → right-aligned to the trigger (bottom-end);
// "right" → left-aligned (bottom-start), matching the previous getBoundingClientRect logic.
const placement = computed<"bottom-start" | "bottom-end">(() => (props.direction === "right" ? "bottom-start" : "bottom-end"));

const closeGroupOnly = () => (activeGroup.value = null);

const defaultItemsOf = (val: GroupValue): any[] => {
  if (Array.isArray(val)) return val;
  if (val && typeof val === "object") return Object.values(val as Record<string, unknown>);
  return [];
};

const defaultGetId = (item: any): string => String(item?.Enum ?? item?.id ?? item);
const defaultGetLabel = (item: any): string => String(item?.DisplayName ?? item?.name ?? item?.Enum ?? item);

const getTagLabel = (tag: string): string => {
  const key = "category." + tag.replace(/\s+/g, "_").toLowerCase();
  const translated = t(key);

  if (translated === key) {
    return tag;
  }

  return translated;
};

const GROUP_CONFIG: Record<string, GroupConfig> = {
  collections: {
    label: "Collections",
    getId: (item) => String(item.id),
    getLabel: (item) => String(item.name),
    showSearch: true,
  },
  units: {
    label: "Unit",
    getId: (item) => String(item.declaredUnit),
    getLabel: (item) => getTagLabel(String(item.displayName)),
    showSearch: false,
  },
  categories: {
    label: "Category",
    getId: (item) => String(item.value),
    getLabel: (item) => getTagLabel(String(item.label)),
    showSearch: props.search,
  },
  datasources: {
    label: "Datasource",
    getId: (item) => String(item.value ?? item.name),
    getLabel: (item) => String(item.label ?? item.name ?? item.id),
    showSearch: props.search,
  },
  owners: {
    label: "Owner",
    getId: (item) => String(item.name),
    getLabel: (item) => String(item.name ?? item.id),
    showSearch: props.search,
  },
  organisations: {
    label: "Organisations",
    getId: (item) => String(item.id),
    getLabel: (item) => String(item.DisplayName),
    showSearch: props.search,
  },
  indicators: {
    label: "Indicators",
    getId: (item) => String(item.Enum),
    getLabel: (item) => String(item.DisplayName ?? item.Enum),
    showSearch: props.search,
  },
  specifications: {
    label: "Specification",
    getId: (item) => String(item.Enum),
    getLabel: (item) => String(item.DisplayName ?? item.Enum),
    showSearch: false,
  },
  types: {
    label: "Type",
    getId: (item) => String(item.Enum),
    getLabel: (item) => String(item.DisplayName ?? item.Enum),
    showSearch: false,
  },
  mappings: {
    label: "Mapping",
    getId: (item) => String(item.value),
    getLabel: (item) => String(item.label),
    showSearch: props.search,
  },
  states: {
    label: "State",
    getId: (item) => String(item.value),
    getLabel: (item) => String(item.label),
    showSearch: props.search,
  },
  calculationMethod: {
    label: "Calculation Method",
    getId: (item) => String(item.Enum),
    getLabel: (item) => String(item.DisplayName ?? item.Enum),
    showSearch: false,
  },
  classificationCodes: {
    label: "Classification",
    getId: (item) => String(item.value),
    getLabel: (item) => String(item.label),
    showSearch: true,
  },
  // Project list filter groups (consumed by ProjectListFilters.vue). Items are `Filter`
  // objects ({ id, label, value, filterGroup, isActive }), so id/label come off those fields.
  calculationType: {
    label: "Calculation type",
    getId: (item) => String(item.id),
    getLabel: (item) => String(item.label),
    showSearch: props.search,
  },
  country: {
    label: "Country",
    getId: (item) => String(item.id),
    getLabel: (item) => String(item.label),
    showSearch: props.search,
  },
  stage: {
    label: "Stage",
    getId: (item) => String(item.id),
    getLabel: (item) => String(item.label),
    showSearch: false,
  },
  role: {
    label: "Role",
    getId: (item) => String(item.id),
    getLabel: (item) => String(item.label),
    showSearch: false,
  },
  type: {
    label: "Type",
    getId: (item) => String(item.id),
    getLabel: (item) => String(item.label),
    showSearch: props.search,
  },
  // AI Agent usage-history filter groups (consumed by HistoryPage.vue). `users` items are
  // MemberOption ({ id, name }); `features` items are plain feature-name strings.
  users: {
    label: "User",
    getId: (item) => String(item.id),
    getLabel: (item) => String(item.name),
    showSearch: true,
  },
  features: {
    label: "Feature",
    getId: (item) => String(item),
    getLabel: (item) => String(item),
    showSearch: props.search,
  },
  // AI Agent Skills visibility facet (consumed by AgentView.vue). Items are { id, label }.
  visibility: {
    label: "Visibility",
    getId: (item) => String(item.id),
    getLabel: (item) => String(item.label),
    showSearch: false,
  },
};

const itemsOf = (group: string, val: GroupValue) => (GROUP_CONFIG[group]?.itemsOf ?? defaultItemsOf)(val);
const getId = (group: string, item: any) => (GROUP_CONFIG[group]?.getId ?? defaultGetId)(item);
const getLabel = (group: string, item: any) => (GROUP_CONFIG[group]?.getLabel ?? defaultGetLabel)(item);

const disabledIdsByGroup = computed(() => {
  const byGroup: Record<string, Set<string>> = {};
  for (const [group, entry] of Object.entries(props.disabledItems ?? {})) byGroup[group] = new Set(entry?.ids ?? []);
  return byGroup;
});
const isPicked = (group: string, id: string) => !!activeFilters.value?.[group]?.[id];
// Disabled blocks picking only: a pick that is already ticked can still be unticked.
const isDisabled = (group: string, id: string) => (disabledIdsByGroup.value[group]?.has(id) ?? false) && !isPicked(group, id);
const isListedLast = (group: string, id: string) => disabledIdsByGroup.value[group]?.has(id) ?? false;
const disabledReasonOf = (group: string) => props.disabledItems?.[group]?.reason ?? "";
const isGroupLoading = (group: string) => props.loadingGroups?.includes(group) ?? false;

const toggleFilter = (group: string, id: string) => {
  if (isDisabled(group, id)) return;
  activeFilters.value[group] ??= {};
  const current = !!activeFilters.value[group][id];
  activeFilters.value[group][id] = !current;
};

const clearAllFilters = () => {
  activeFilters.value = {};
  emit("clear");
};

const clearGroupFilters = (group: string) => {
  if (!(group in activeFilters.value)) return;
  delete activeFilters.value[group];
};

const countActiveInGroup = (group: string) =>
  activeFilters.value[group] ? Object.values(activeFilters.value[group]).filter(Boolean).length : 0;

const totalActiveCount = computed(() => {
  // Count filters in groups that are in GROUP_CONFIG
  const groupFiltersCount = Object.entries(activeFilters.value)
    .filter(([groupKey]) => groupKey in GROUP_CONFIG)
    .flatMap(([, group]) => Object.values(group))
    .filter(Boolean).length;

  // Count boolean flags that are direct properties (like minValidTo, favoritesActive)
  const booleanFlagsCount = Object.entries(activeFilters.value).filter(
    ([groupKey, group]) => !(groupKey in GROUP_CONFIG) && typeof group === "boolean" && group
  ).length;

  return groupFiltersCount + booleanFlagsCount;
});

const totalActiveCountWithExtras = computed(() => totalActiveCount.value + (props.extraActiveCount ?? 0));

const filteredItems = (groupKey: string, groupVal: GroupValue, search: string) => {
  const filtered = itemsOf(groupKey, groupVal).filter((it) =>
    getLabel(groupKey, it).toLowerCase().includes(search.toLowerCase())
  );
  const rank = (item: unknown) => (isListedLast(groupKey, getId(groupKey, item)) ? 1 : 0);
  return useSorted(filtered, (a, b) => rank(a) - rank(b) || getLabel(groupKey, a).localeCompare(getLabel(groupKey, b))).value;
};
</script>

<template>
  <Popover
    :placement="placement"
    :teleport="teleport"
    :panel-z="panelZ"
    :aria-label="title ?? 'Filters'"
    panel-class="w-64 flex flex-col">
    <template #trigger="{ toggle, isOpen }">
      <button
        :id="triggerId"
        v-tooltip="{ text: title ?? 'Filters', variant: 'label', placement: 'bottom' }"
        type="button"
        :aria-label="title ?? 'Filters'"
        :aria-expanded="isOpen"
        aria-haspopup="dialog"
        class="rounded-8 bg-slate-0 text-type-default focus-ring interactive-active duration-fast ease-standard ring-slate-250 relative inline-flex shrink-0 items-center justify-center ring-1 transition-all hover:bg-slate-100 hover:ring-slate-300"
        :class="triggerSizeClass ?? 'p-px-8 h-fit'"
        @click="toggle">
        <Notifications :notifications="totalActiveCountWithExtras" />
        <Funnel
          class="icon-sm shrink-0"
          :stroke-width="1.5"
          color="var(--color-app-blue-800)"
          fill="var(--color-app-blue-100)" />
      </button>
    </template>

    <div class="flex items-center justify-between border-b border-slate-200 px-3 py-2">
      <span class="t-caption-bold text-type-muted uppercase">Filters</span>
      <button
        type="button"
        class="t-caption text-type-muted hover:text-type-default transition-colors"
        @click="clearAllFilters">
        Clear
      </button>
    </div>

    <div class="flex flex-col gap-1 p-2">
      <slot />

      <div id="filterGroup">
        <template
          v-for="(groupVal, groupKey) in filterGroups"
          :key="groupKey">
          <FilterGroup v-if="groupKey in GROUP_CONFIG">
            <FilterGroupHeader
              :is-active="activeGroup === groupKey"
              :name="props.groupLabels?.[groupKey] ?? groupKey"
              :direction="direction"
              @on-toggle="() => (activeGroup = activeGroup === groupKey ? null : groupKey)" />
            <Notifications :notifications="countActiveInGroup(groupKey)" />

            <FilterGroupPanel
              v-show="activeGroup === groupKey"
              :key-name="groupKey"
              :label="props.groupLabels?.[groupKey] ?? GROUP_CONFIG[groupKey]?.label"
              :is-visible="activeGroup === groupKey"
              :show-search="GROUP_CONFIG[groupKey]?.showSearch ?? false"
              :direction="direction"
              @close="closeGroupOnly"
              @clear="clearGroupFilters(groupKey)">
              <template #default="{ search: searchTerm }">
                <div
                  v-if="isGroupLoading(groupKey)"
                  class="flex justify-center py-1">
                  <LoadingSpinner
                    size="sm"
                    fill="primary" />
                </div>
                <template v-else-if="filteredItems(groupKey, groupVal, searchTerm).length">
                  <FilterOption
                    v-for="item in filteredItems(groupKey, groupVal, searchTerm)"
                    :key="getId(groupKey, item)"
                    :is-active="!!activeFilters?.[groupKey]?.[getId(groupKey, item)]"
                    :name="getLabel(groupKey, item)"
                    :disabled="isDisabled(groupKey, getId(groupKey, item))"
                    :disabled-reason="disabledReasonOf(groupKey)"
                    @toggle-filter="toggleFilter(groupKey, getId(groupKey, item))" />
                </template>
                <span
                  v-else
                  class="t-xs text-type-muted px-2 py-1">
                  No options
                </span>
              </template>
            </FilterGroupPanel>
          </FilterGroup>
        </template>
      </div>
    </div>
  </Popover>
</template>
